import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useBooking } from '../hooks/useBooking';
import type { FormDefinition, FormResponsePlaintext } from '../types/forms';
import { getApplicableForms, getFormEncryptionKey, submitFormResponse } from '../utils/formApi';
import { encryptFormResponse } from '../utils/formEncryption';
import { executeRecaptchaV3 } from '../utils/recaptcha';
import { FormRenderer } from '../components/forms/FormRenderer';
import { BookingStepper } from '../components/BookingStepper';

export function BookingFormsPage() {
  const navigate = useNavigate();
  const { businessListing, bookingState } = useBooking();

  const [forms, setForms] = useState<FormDefinition[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [failCount, setFailCount] = useState(0);
  const [publicKeyB64, setPublicKeyB64] = useState<string | null>(null);

  const practitionerUid = businessListing?.id ?? '';
  const serviceId = bookingState.selectedService?.id ?? '';
  const customerInfo = bookingState.customerInfo;

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!practitionerUid || !serviceId) {
        navigate('/book/confirm', { replace: true });
        return;
      }

      const [applicableForms, keyData] = await Promise.all([
        getApplicableForms(practitionerUid, serviceId),
        getFormEncryptionKey(practitionerUid),
      ]);

      if (cancelled) return;

      if (!keyData || applicableForms.length === 0) {
        if (!keyData) {
          console.warn('No form encryption key — skipping forms');
        }
        navigate('/book/confirm', { replace: true });
        return;
      }

      setForms(applicableForms);
      setPublicKeyB64(keyData.publicKeyB64);
      setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, [practitionerUid, serviceId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-site-bg text-site-text">
        <BookingStepper currentStep={4} hasFormsStep />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-site-muted" />
        </div>
      </div>
    );
  }

  const form = forms[currentIndex];
  const isLast = currentIndex === forms.length - 1;
  const isFirst = currentIndex === 0;

  const handleBack = () => {
    if (!isFirst) {
      setCurrentIndex(i => i - 1);
      setSubmitError(null);
      setFailCount(0);
    } else {
      navigate('/book/details');
    }
  };

  const handleSkip = () => {
    if (isLast) {
      navigate('/book/confirm');
    } else {
      setCurrentIndex(i => i + 1);
      setSubmitError(null);
      setFailCount(0);
    }
  };

  const handleSubmit = async (data: FormResponsePlaintext) => {
    if (!form || !publicKeyB64) return;
    setSubmitError(null);
    setSubmitting(true);

    try {
      const encrypted = encryptFormResponse(data, publicKeyB64);

      let recaptchaToken: string | undefined;
      try {
        recaptchaToken = await executeRecaptchaV3('form_submit');
      } catch {
        setSubmitError("We couldn't verify your request. Please reload and try again.");
        setSubmitting(false);
        return;
      }

      const result = await submitFormResponse({
        practitionerId: practitionerUid,
        recaptchaToken,
        responses: [{
          formId: form.id,
          formType: form.type,
          ciphertext: encrypted.ciphertextB64,
          nonce: encrypted.nonceB64,
          ephemeralPublicKey: encrypted.ephemeralPublicKeyB64,
        }],
      });

      if (result.success) {
        setFailCount(0);
        if (isLast) {
          navigate('/book/confirm');
        } else {
          setCurrentIndex(i => i + 1);
          setSubmitError(null);
        }
      } else {
        const nextFail = failCount + 1;
        setFailCount(nextFail);
        setSubmitError(result.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      const nextFail = failCount + 1;
      setFailCount(nextFail);
      console.error('Form submission error:', err);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const showEscapeHatch = failCount >= 2;

  return (
    <div className="min-h-screen bg-site-bg text-site-text pb-10">
      <BookingStepper currentStep={4} hasFormsStep />

      <div className="max-w-xl mx-auto px-4 py-8">
        {/* Back + progress */}
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1 text-sm text-site-muted hover:text-site-text transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            {isFirst ? 'Back to Details' : 'Previous'}
          </button>

          {forms.length > 1 && (
            <span className="text-sm text-site-muted">
              Form {currentIndex + 1} of {forms.length}
            </span>
          )}
        </div>

        <div className="bg-site-bg rounded-2xl border border-site-border p-6">
          {submitError && (
            <div className="mb-6 p-4 bg-site-danger-soft border border-site-danger/40 rounded-xl">
              <p className="text-sm text-site-danger">{submitError}</p>
            </div>
          )}

          <FormRenderer
            form={form}
            contactInfo={{
              firstName: customerInfo.firstName,
              lastName: customerInfo.lastName,
              phone: customerInfo.phone,
              countryCode: customerInfo.countryCode,
            }}
            submitLabel={isLast ? 'Continue' : 'Next'}
            onSubmit={handleSubmit}
            submitting={submitting}
          />

          {!form.required && (
            <button
              type="button"
              onClick={handleSkip}
              className="mt-3 w-full text-sm text-site-muted hover:text-site-text py-2 transition-colors"
            >
              Skip this form
            </button>
          )}

          {showEscapeHatch && form.required && (
            <button
              type="button"
              onClick={handleSkip}
              className="mt-3 w-full text-sm text-site-muted hover:text-site-text py-2 transition-colors"
            >
              Skip and continue booking
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
